import ErrorFallback from 'components/error-fallback/ErrorFallback';
import useCompilerPdfLogic from 'components/v2/compiler/useCompilerPdfLogic';
import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import AuthService from 'services/AuthService';
import { useGetEpdQRCodePDF } from 'services/api/queries';
import AdditionalInfoPage from './AdditionalInfoPage';
import BackCoverPage from './BackCoverPage';
import ContentDeclarationPage from './ContentDeclarationPage';
import CoverPage from './CoverPage';
import LCACustomResultsPage from './LCACustomResultsPage';
import LCAInformationPage from './LCAInformationPage';
import LCAResultsPage from './LCAResultsPage';
import ManufacturerInformationPage from './ManufacturerInformationPage';
import OwnershipPage from './OwnershipPage';
import ProcessFlowDiagramsDescriptionPage from './ProcessFlowDiagramsDescriptionPage';
import ProductInformationPage from './ProductInformationPage';
import ProgrammeInformationPage from './ProgrammeInformationPage';
import ReferencesPage from './ReferencePage';
import ScenariosPage from './ScenariosPage';
import SystemBoundaryPage from './SystemBoundaryPage';
import TechPerformancePage from './TechPerformancePage';
import { CONSTRUCTION_PCR_V2_ID } from 'constants/constants';
import { PcrV2Context } from 'components/v2/compiler/hooks/usePcrV2';
import usePdfDataLoader from './utils/usePdfDataLoader';
import { PdfDataContext } from './utils/PdfDataContext';

const EpdCompilerPdfPreviewAuth: React.FunctionComponent = () => {
  const { epdVersionId } = useCompilerPdfLogic();
  const pdfData = usePdfDataLoader(epdVersionId);

  const { data: qrImageBlob } = useGetEpdQRCodePDF(
    pdfData?.epdDefinition.id ?? null,
    pdfData ? `epd${pdfData.epdDefinition.registrationNumber}` : null,
    pdfData?.epdDefinition.fullIdentificationNumber ?? null
  );

  const qrImageUrl = qrImageBlob?.data ? URL.createObjectURL(qrImageBlob.data as Blob) : null;
  useEffect(() => {
    return () => {
      if (qrImageUrl) {
        URL.revokeObjectURL(qrImageUrl);
      }
    };
  }, [qrImageUrl]);

  if (!AuthService.isLoggedin()) {
    return null;
  }

  if (!pdfData) {
    return <div>Loading data...</div>;
  }

  const { pcrInformation, products } = pdfData;
  const isPcrV2 = !pcrInformation.pcr || pcrInformation.pcr.id === CONSTRUCTION_PCR_V2_ID;

  return (
    <PdfDataContext.Provider value={pdfData}>
      <PcrV2Context.Provider value={isPcrV2}>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <CoverPage qrImageUrl={qrImageUrl} />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <ProgrammeInformationPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <OwnershipPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <ManufacturerInformationPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          {products.map((product, index) => (
            <ProductInformationPage
              key={index}
              productInformation={product}
              shouldShowProductsStatusesSection={index === 0}
            />
          ))}
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <TechPerformancePage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <ContentDeclarationPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <LCAInformationPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <SystemBoundaryPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <ProcessFlowDiagramsDescriptionPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <ScenariosPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <LCAResultsPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <LCACustomResultsPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <AdditionalInfoPage />
        </ErrorBoundary>

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <ReferencesPage />
        </ErrorBoundary>
        <BackCoverPage />
      </PcrV2Context.Provider>
    </PdfDataContext.Provider>
  );
};

export default EpdCompilerPdfPreviewAuth;
